import type { Alignment, Size, SeparatorStyle, NumberStyle } from './types';

export const ALIGNMENT_CLASSES: Record<Alignment, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export const FLEX_ALIGNMENT_CLASSES: Record<Alignment, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

export const SIZE_CLASSES: Record<Size, string> = {
  small: 'py-12 md:py-16',
  medium: 'py-16 md:py-24',
  large: 'py-20 md:py-32',
};

export const HEADLINE_SIZE_CLASSES: Record<Size, string> = {
  small: 'text-2xl md:text-3xl',
  medium: 'text-3xl md:text-4xl',
  large: 'text-4xl md:text-5xl',
};

export const SUBHEADLINE_SIZE_CLASSES: Record<Size, string> = {
  small: 'text-base md:text-lg',
  medium: 'text-lg md:text-xl',
  large: 'text-xl md:text-2xl',
};

export const NUMBER_SIZE_CLASSES: Record<Size, string> = {
  small: 'text-2xl md:text-3xl',
  medium: 'text-4xl md:text-5xl',
  large: 'text-6xl md:text-7xl',
};

export function formatNumber(num: number): string {
  return num.toString().padStart(2, '0');
}

export function getSeparator(style: SeparatorStyle | undefined): string {
  switch (style) {
    case 'colon':
      return ':';
    case 'slash':
      return '/';
    case 'dot':
      return '·';
    case 'none':
      return '';
    default:
      return ':';
  }
}

export function getNumberStyleClasses(style: NumberStyle | undefined): string {
  const baseClasses = 'font-bold';
  switch (style) {
    case 'bold':
      return `${baseClasses} font-black`;
    case 'card':
      return `${baseClasses} bg-white px-3 py-2 rounded-lg shadow-sm`;
    case 'gradient':
      return `${baseClasses} bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent`;
    default:
      return baseClasses;
  }
}

export function getBackgroundPosition(position?: string): string {
  if (!position) return 'center center';
  const positionMap: Record<string, string> = {
    center: 'center center',
    top: 'center top',
    bottom: 'center bottom',
    left: 'left center',
    right: 'right center',
    'top-left': 'left top',
    'top-right': 'right top',
    'bottom-left': 'left bottom',
    'bottom-right': 'right bottom',
  };
  return positionMap[position] || 'center center';
}

export function isGradient(bg: string): boolean {
  return bg?.startsWith('linear-gradient') || bg?.startsWith('radial-gradient');
}

export function getBackgroundStyle(
  data: {
    backgroundImage?: { enabled?: boolean };
    backgroundColor?: string;
    customColors?: { background?: string };
  },
  themeBg?: string
): React.CSSProperties {
  if (data.backgroundImage?.enabled) {
    return {};
  }

  // PRIORITY: customColors > deprecated fields > theme
  const bgColor = data.customColors?.background || data.backgroundColor;
  if (bgColor) {
    return { backgroundColor: bgColor };
  }

  if (themeBg) {
    if (isGradient(themeBg)) {
      return { backgroundImage: themeBg };
    } else {
      return { backgroundColor: themeBg };
    }
  }

  return {};
}
